import React, { useRef, useMemo, useState, useEffect } from 'react';
import Utils from '../utils/utils';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { fetch } from '../utils/api-wrapper';
import axios from 'axios';
import Switch from 'react-switch';
import ImportTransactionsModal from '../components/import-transactions-modal/import-transactions-modal';

const CcardForm = ({ data, ccard, client, setCcard, updateCcard, createCcard, setShowEditModal, refreshCcards }) => {
  const cancelToken = useMemo(() => axios.CancelToken.source(), []);
  const formRef = useRef(null);
  const cardMaskedNumberRef = useRef(null);
  const cardTypeRef = useRef(null);
  const supplierRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const cycleDays = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
  const cycleDateRef = useRef(null);
  const [cardOwners, setCardOwners] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const loadSuppliers = (search_query = '', callback = () => {}) => {
    // fetch suppliers from the api search by name or supplier number
    const filters = [{ id: 'id_client', value: client?._id ?? data.id_client }];
    if (search_query.length > 0) {
      filters.push({ id: 'search', value: search_query });
    }
    const sorting = [{ id: 'names', desc: false }];
    const pageSize = 20;
    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(
      JSON.stringify(sorting ?? [])
    )}&page_size=${pageSize}`;
    fetch(
      `/suppliers/${searchData}`,
      'GET',
      {},
      (response) => {
        callback(response?.data?.suppliers?.map((s) => ({ label: s.name ?? s.names?.[0] ?? '', value: s._id?.$oid })) ?? []);
      },
      null,
      false,
      cancelToken.token
    );
  };

  const loadBankAcctAP = (search_query = '', callback = () => {}) => {
    const val = (search_query || '').trim().replace(/\//g, '');
    const filters = val.length > 0 ? [{ id: 'description', value: val }] : [];
    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}`;
    fetch(
      `/bankacctap/${searchData}`,
      'GET',
      {},
      (response) => {
        const options =
          (response?.data?.bankacctap || []).map((p) => ({ label: p.description, value: { _id: p._id.$oid, description: p.description } })) ?? [];
        callback(options.slice(0, 10));
      },
      null,
      false,
      cancelToken.token
    );
  };

  const validateCcard = () => {
    const inputs = Array.from(formRef.current.querySelectorAll('input, textarea, select'));
    for (const input of inputs) {
      if (input.required || input.type === 'email' || input.minLength || input.maxLength) {
        if (!input.checkValidity()) {
          input.reportValidity();
          input.focus();
          return false;
        }
      }
    }
    return true;
  };

  const saveCcard = () => {
    if (validateCcard() === false) {
      return;
    }
    setSaving(true);

    if (ccard._id) {
      updateCcard(cardOwners, () => setSaving(false));
    } else {
      createCcard(cardOwners, () => setSaving(false));
    }
  };

  const fetchCardOwners = () => {
    if (!ccard?._id?.$oid) {
      return;
    }
    fetch(
      `/c_card/owners/${ccard._id.$oid}`,
      'GET',
      {},
      (response) => {
        const owners = response?.data?.map((o) => ({ label: `${o.first_name} ${o.last_name}`, value: o._id?.$oid })) ?? [];
        setCardOwners([...owners]);
      },
      null,
      false,
      cancelToken.token
    );
  };

  useEffect(() => {
    fetchCardOwners();
  }, []);

  const loadUsers = (search_query = '', callback = () => {}) => {
    const filters = [{ id: 'id_client', value: client?._id ?? data.id_client }];

    if (search_query.length > 0) {
      filters.push({ id: 'search', value: search_query });
    }
    const sorting = [{ id: 'name', desc: false }];
    const pageSize = 20;

    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(
      JSON.stringify(sorting ?? [])
    )}&page_size=${pageSize}`;

    fetch(
      `/users/${searchData}`,
      'GET',
      {},
      (response) => {
        const users = response?.data?.users ?? [];

        callback(
          users.map((u) => ({
            label: `${u.first_name} ${u.last_name}`,
            value: u._id?.$oid,
          })) ?? []
        );
      },
      null,
      false,
      cancelToken.token
    );
  };

  const updateCardOwners = (selectedOptions) => {
    setCardOwners(selectedOptions);
  };

  return (
    <>
      <form ref={formRef}>
        <div className='form-group d-flex align-items-center'>
          <div className='form-label col-4'>{data.t.read('cc_transactions.column_names.account_mask')}</div>
          <input
            className='form-control col-8 m-0 py-4'
            required
            placeholder={data.t.read('cc_transactions.column_names.account_mask')}
            type='text'
            maxLength={4}
            minLength={4}
            value={ccard?.card_masked_number ?? ''}
            onChange={(e) => setCcard({ ...ccard, card_masked_number: e.target.value })}
            ref={cardMaskedNumberRef}
          />
        </div>
        <div className='form-group d-flex align-items-center'>
          <div className='form-label col-4'>{data.t.read('label')}</div>
          <input
            className='form-control col-8 m-0 py-4'
            placeholder={data.t.read('label')}
            type='text'
            value={ccard?.label ?? ''}
            onChange={(e) => setCcard({ ...ccard, label: e.target.value })}
          />
        </div>
        <div className='form-group d-flex mb-3 align-items-center'>
          <div className='form-label col-4 '>{data.t.read('cc_statements.column_names.card_type')}</div>
          <div className='col-8 p-0'>
            <Select
              name='card_type'
              required
              ref={cardTypeRef}
              value={ccard?.card_type ? { value: ccard.card_type, label: data.t.read(`${ccard.card_type.toLowerCase()}`) } : null}
              options={['p-card', 'personal-card'].map((s) => ({ label: data.t.read(`${s.toLowerCase()}`), value: s }))}
              styles={Utils.styleSelect}
              classNamePrefix='select'
              placeholder={data.t.read('cc_statements.column_names.card_type')}
              onChange={(c) => setCcard({ ...ccard, card_type: c.value })}
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          </div>
        </div>

        <div className='form-group d-flex mb-3 align-items-center'>
          <div className='form-label col-4'>{data.t.read('transaction_type.default_title')}</div>
          <div className='col-8 p-0'>
            <Select
              name='default_transaction_type'
              value={{
                value: ccard?.default_transaction_type || 'expense_invoice',
                label: data.t.read(`transaction_type.${ccard?.default_transaction_type || 'expense_invoice'}`),
              }}
              options={['invoice', 'expense_invoice'].map((transactionType) => ({
                value: transactionType,
                label: data.t.read(`transaction_type.${transactionType}`),
              }))}
              styles={Utils.styleSelect}
              classNamePrefix='select'
              onChange={(choice) => setCcard({ ...ccard, default_transaction_type: choice.value })}
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          </div>
        </div>

        <div className='form-group d-flex align-items-center'>
          <div className='form-label col-4 '>{data.t.read('supplier_name')}</div>
          <div className='col-8 p-0'>
            <AsyncSelect
              required
              value={ccard?.supplier ? { label: ccard.supplier.name, value: ccard.supplier._id } : null}
              onChange={(selectedOption) =>
                setCcard({ ...ccard, supplier: selectedOption ? { name: selectedOption.label, _id: selectedOption.value } : null })
              }
              defaultOptions
              cacheOptions
              loadOptions={loadSuppliers}
              placeholder={data.t.read('suppliers')}
              isClearable={true}
              ref={supplierRef}
              styles={Utils.styleSelect}
              classNamePrefix='select'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          </div>
        </div>

        <div className='form-group d-flex align-items-center'>
          <div className='form-label col-4 '>AP {data.t.read('bank')}</div>
          <div className='col-8 p-0'>
            <AsyncSelect
              name='bankacctap'
              value={ccard?.bankacctap ? { label: ccard.bankacctap.description, value: ccard.bankacctap } : null}
              defaultOptions
              cacheOptions
              loadOptions={loadBankAcctAP}
              placeholder={`AP ${data.t.read('bank')}`}
              isClearable
              styles={Utils.styleSelect}
              classNamePrefix='select'
              onChange={(choice) => setCcard({ ...ccard, bankacctap: choice?.value ?? null })}
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          </div>
        </div>
        {data.config?.payment_modes && (
          <div className='form-group d-flex align-items-center'>
            <div className='form-label col-4 '>{data.t.read('payment_mode')}</div>
            <div className='col-8 p-0'>
              <Select
                name='payment_mode'
                value={ccard?.payment_mode || null}
                options={data.config.payment_modes ? data.config.payment_modes.map((p) => ({ value: p.id, label: p.name })) : []}
                styles={Utils.styleSelect}
                classNamePrefix='select'
                placeholder={data.t.read('payment_mode')}
                isClearable
                onChange={(choice) => setCcard({ ...ccard, payment_mode: choice })}
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            </div>
          </div>
        )}
        <div className='form-group d-flex mb-3 align-items-center'>
          <div className='form-label col-4 '>{data.t.read('cc_transactions.cycle_date')}</div>
          <div className='col-8 p-0'>
            <Select
              name='cycle_date'
              required
              ref={cycleDateRef}
              value={ccard?.cycle_start_day ? { value: ccard.cycle_start_day, label: ccard.cycle_start_day } : null}
              options={cycleDays.map((d) => ({ label: d, value: d }))}
              styles={Utils.styleSelect}
              classNamePrefix='select'
              placeholder={data.t.read('cc_transactions.cycle_date')}
              onChange={(c) => setCcard({ ...ccard, cycle_start_day: c.value })}
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          </div>
        </div>
        <div className='form-group d-flex align-items-center'>
          <div className='form-label col-4'>{data.t.read('owners')}</div>
          <div className='col-8 p-0'>
            <AsyncSelect
              name='owners'
              value={cardOwners ?? null}
              defaultOptions
              selectedOptions={cardOwners}
              cacheOptions
              loadOptions={loadUsers}
              placeholder={data.t.read('owners')}
              isMulti
              isClearable
              onChange={(value) => updateCardOwners(value)}
              styles={Utils.styleSelect}
              classNamePrefix='select'
            />
          </div>
        </div>
        <div className='form-group d-flex align-items-center'>
          <div className='form-label col-4'>{data.t.read('active')}</div>
          <div className='p-0'>
            <Switch
              className='switch'
              checked={ccard?.active ?? true}
              onColor='#F39800'
              onHandleColor='#2693e6'
              onChange={(active) => setCcard({ ...ccard, active: active })}
            />
          </div>
        </div>
        <div className='form-group d-flex align-items-center mx-2'>
          <button className='btn btn-primary mx-2 justify-content-end' type='button' onClick={() => setShowImportModal(true)}>
            {data.t.read('cc_transactions.import_transactions')}
          </button>
        </div>
        <div className='d-flex modal-footer justify-content-between'>
          <button
            type='button'
            className='btn btn-secondary'
            onClick={() => {
              setShowEditModal(false);
              setCcard(null);
            }}
          >
            {data.t.read('cancel')}
          </button>
          <button type='submit' disabled={saving} className='btn btn-primary' onClick={() => saveCcard()}>
            {data.t.read('save')}
          </button>
        </div>
      </form>
      <ImportTransactionsModal
        isOpen={showImportModal}
        data={data}
        onClose={() => {
          setShowImportModal(false);
        }}
        refreshCcards={refreshCcards}
        fileFormatExample={data.t.read('cc_transactions.csv_example_single_card')}
        accountMask={ccard?.card_masked_number ?? ''}
      />
    </>
  );
};

export default CcardForm;
